/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EndNode.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { Handle, Position } from "reactflow";
import { registerNode } from "../registry/NodeRegistry";
import { useState } from "react";
import NodeToolbar from "./NodeToolbar";

function EndNode({ data, selected }) {
  const { nodeId, outcome, updateNodeData } = data;

  const [hovered, setHovered] = useState(false);

  const setOutcome = (_, value) => {
    if (!value) return;
    updateNodeData(nodeId, {
      outcome: { ...outcome, value },
    });
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: "relative" }}
    >
      <NodeToolbar
        visible={hovered || selected}
        onDelete={() => data.onDeleteNode(nodeId)}
      />
      <Box
        className="pm-node-wrapper"
        sx={{
          display: "flex",
          width: 480,
          bgcolor: "#f5f5f5",
          border: "1px solid",
          borderColor: selected ? "#b71c1c" : "#2b2b2b",
          boxShadow: "0 10px 10px rgba(0,0,0,0.5)",
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box sx={{ width: 6, bgcolor: "#b71c1c", marginLeft: "auto" }} />

        <Box sx={{ flex: 1, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StopCircleOutlinedIcon fontSize="small" color="error" />
            <Typography fontWeight={600}>End</Typography>
          </Box>

          <Box
            sx={{
              mt: 1,
              alignItems: "start",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Ticket outcome
            </Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={outcome.value}
              onChange={setOutcome}
              sx={{
                mt: 0.5,
                gap: 1,
              }}
            >
              <ToggleButton value="CREATE">
                <Typography variant="h6" color="text.primary">
                  Create Ticket
                </Typography>
              </ToggleButton>
              <ToggleButton value="DO_NOT_CREATE">
                <Typography variant="h6" color="text.primary">
                  Do Not Create Ticket
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Handle
          type="target"
          position={Position.Left}
          id="in"
          className="pm-input-handle"
        />
      </Box>
    </Box>
  );
}

registerNode({
  type: "end",
  label: "End",

  getDefaultData() {
    return {
      outcome: {
        type: "TICKET_DECISION",
        value: "CREATE",
      },
    };
  },

  renderNode: EndNode,
});

EndNode.propTypes = {
  data: PropTypes.shape({
    nodeId: PropTypes.string.isRequired,
    outcome: PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string.isRequired,
    }).isRequired,
    updateNodeData: PropTypes.func.isRequired,
    onDeleteNode: PropTypes.func,
  }).isRequired,
  selected: PropTypes.bool,
};

export default EndNode;
